import { isBoolean } from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useFormContext } from '@/components/react-hook-form';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import { getIntegrationEntitiesFromEntity } from '@/modules/assetProviderIntegrations/shared/utils';
import { STRUCTURED_ASSETS_SUBFORM_NAMESPACE } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import {
  getStructuredAssetsSubformValues,
  valuationHasAdditionalManualAssets,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { getNodes } from '@/utils/graphqlUtils';

import { FullScreenStructuredAssetsModalForm } from '../FullScreenStructuredAssetsModal.types';
import { useFullScreenStructuredAssetsModalQuery } from '../graphql/FullScreenStructuredAssetsModal.generated';

export function useFullScreenStructuredAssetFormData(
  entityId: string,
  onNotFound: () => void
) {
  const { reset } = useFormContext<FullScreenStructuredAssetsModalForm>();
  const [completedLoadingFormData, setCompletedLoadingFormData] =
    useState(false);
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { recoverExpectedNotFound, recoverMissingRecord } =
    useRecordNotFoundHandler({
      message: 'This entity is no longer available.',
      onNotFound,
    });
  const { data, ...queryProps } = useFullScreenStructuredAssetsModalQuery({
    // the underlying FormAwareInputRepeater will initialize the form values to empty,
    // but if we have two data values returning (cache, then network), then the empty/initialized
    // inputrepeater values will be overwritten with an empty array from the cache, then never
    // re-initialized
    fetchPolicy: 'network-only',
    variables: {
      entityId,
    },
    onError: (err) => {
      if (recoverExpectedNotFound(err)) {
        return;
      }

      showFeedback('Failed to fetch asset details');
      reportError('could not fetch structured asset details', err);
    },
  });

  const entity = (() => {
    if (data?.node?.__typename !== 'Entity') return null;
    return data.node;
  })();

  const designerAccount = entity?.subtype.designerAccount ?? null;
  const valuation = designerAccount?.valuations.edges?.[0]?.node ?? null;

  useEffect(() => {
    if (data && !entity) {
      recoverMissingRecord();
    }
  }, [data, entity, recoverMissingRecord]);

  useEffect(() => {
    if (!entity || completedLoadingFormData) return;
    const hasAdditionalManualAssets =
      valuationHasAdditionalManualAssets(valuation);

    const assetSubformValues =
      getStructuredAssetsSubformValues(designerAccount);

    const valuationAsOfDate = valuation?.effectiveDate ?? new Date();

    const currentIntegrationEntityIds = getIntegrationEntitiesFromEntity(
      entity
    ).map((ie) => ie.id);

    reset({
      [STRUCTURED_ASSETS_SUBFORM_NAMESPACE]: assetSubformValues,
      valuationAsOfDate: valuationAsOfDate,
      _previousIntegrationEntityIds: currentIntegrationEntityIds,
      integrationEntityIds: currentIntegrationEntityIds,
      pauseIntegrationSync: entity.pauseIntegrationSync,
      linkToAllAddeparEntities: entity.addeparLinkedToNongrantorEntity ?? false,
      allowAdditionalManualAssets: hasAdditionalManualAssets,
      balanceSheetCategoryOverrideId:
        entity.balanceSheetCategoryOverride?.id ?? '',
      showBalanceSheetCategoryOverrideChooser: Boolean(
        entity.balanceSheetCategoryOverride?.id
      ),
      showEntityNameOnBalanceSheet:
        entity.showEntityNameOnBalanceSheet ?? false,
      showCurrentDiscountedValueOnBalanceSheet:
        entity.subtype.taxDiscount?.greaterThan(0) || false,
      currentDiscountedValuePercentage: entity.subtype.taxDiscount || null,
      showAllIntegratedHoldings: isBoolean(entity.groupIntegrationAssetValues)
        ? !entity.groupIntegrationAssetValues
        : false,
      liquidityStatus: entity.liquidityStatus,
      enableRealTppTaxExposure:
        ('enableRealTppTaxExposure' in entity.subtype &&
          entity.subtype.enableRealTppTaxExposure) ||
        false,
    });
    setCompletedLoadingFormData(true);
  }, [completedLoadingFormData, designerAccount, entity, reset, valuation]);

  const balanceSheetCategories = useMemo(
    () => getNodes(data?.balanceSheetCategories),
    [data?.balanceSheetCategories]
  );

  return {
    data,
    entity,
    valuation,
    balanceSheetCategories,
    ...queryProps,
    loading: queryProps.loading || !completedLoadingFormData,
  };
}
